import { useEffect, useRef, useState, type FormEvent, type ReactNode } from "react";
import { ErrorLines } from "@/components/ErrorAlert";
import { StatusBadge } from "@/components/StatusBadge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { NativeSelect } from "@/components/ui/native-select";
import { Textarea } from "@/components/ui/textarea";
import { ApiFailure, createManualEntry, errorText, getUpload } from "@/lib/client";
import { checkFields, formFields, isRequired, toValue, type Cells } from "@/lib/manual";
import { go, uploadHref } from "@/lib/nav";
import { FIELD_HINT, fieldLabel, reasonLabel, unitOptions, valueOptions } from "@/lib/upload";
import { ENUMS } from "../../shared/normalize";
import type { FieldValue, RowError, UploadOrigin } from "../../shared/api";
import { SOURCE_TYPES, type SourceType } from "../../shared/rows";
import { sourceTypeText } from "../../shared/sources";
import { versionLabel } from "../../shared/upload";
import { formatNumber, parseRecordKey } from "../../shared/versions";
import { recordIdText } from "./UploadDetailScreen";

/** Pencerenin açıldığı yer. `record` varsa düzeltme (kayıt ve son versiyondaki değerleri), yoksa yeni kayıt. `basedOn`: tipin son versiyonu, hiç yoksa null. */
export type ManualTarget = {
  sourceType: SourceType;
  basedOn: { uploadId: number; origin: UploadOrigin } | null;
  record?: { recordKey: string; fields: Record<string, FieldValue> };
};

/** Başarıda Yükleme detayına `history.state` ile taşınan sonuç satırı (ui.md §11.4). Sayılar ve kimlik yanıttandır. */
export type ManualNotice = { uploadId: number; operation: "create" | "correct"; count: number; recordKey: string; sourceType: SourceType };

const INFO = {
  correct: "Kayıt yeni bir versiyon olarak saklanır. Eski hâl Veri Geçmişi'nde kalır. Sonraki Excel yüklemesi bu değeri değiştirir.",
  create: "Kayıt yeni bir versiyon olarak eklenir. Sonraki Excel yüklemesinde bu kayıt yoksa kaldırılmış olarak görünür.",
};
const LINK = "text-primary underline underline-offset-4 outline-none focus-visible:ring-[3px] focus-visible:ring-ring rounded-sm";

type Fail = { kind: "fields" } | { kind: "text"; text: string } | { kind: "conflict"; latestId: number; label: string };

const show = (v: FieldValue) => (v === null ? "" : typeof v === "number" ? formatNumber(v) : v);

export function ManualEntryDialog({ target, onClose }: { target: ManualTarget; onClose: () => void }) {
  const t = target.sourceType, spec = SOURCE_TYPES[t], kinds = spec.kinds as Record<string, string>;
  const correct = !!target.record;
  const { key, rest } = formFields(t);
  const inputs = correct ? rest : [...key, ...rest];
  const parts = target.record ? parseRecordKey(target.record.recordKey) : [];

  // Form değerleri metindir; dokunulmayan alan ilk JSON değerini korur (formatNumber 4 hanede keser).
  const init = useRef(Object.fromEntries(inputs.map((f) => [f, correct ? show(target.record!.fields[f] ?? null) : ""]))).current;
  const [text, setText] = useState<Record<string, string>>(init);
  const [reason, setReason] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [fail, setFail] = useState<Fail | null>(null);
  const [busy, setBusy] = useState(false);
  const [forbidden, setForbidden] = useState(false);
  const [tick, setTick] = useState(0);
  const form = useRef<HTMLFormElement>(null);
  const summary = useRef<HTMLDivElement>(null);

  const value = (f: string): FieldValue => (correct && text[f] === init[f] ? target.record!.fields[f] ?? null : toValue(t, f, text[f] ?? ""));
  const cells = (): Cells => ({
    ...(correct ? Object.fromEntries(key.map((k) => [k, target.record!.fields[k] ?? parts[(spec.key as readonly string[]).indexOf(k)] ?? null])) : {}),
    ...Object.fromEntries(inputs.map((f) => [f, value(f)])),
  });

  // Başarısız gönderimden sonra odak hata özetine gider; özet hatalı alanlara bağlantı verir (ui.md §11.4).
  useEffect(() => {
    if (!tick) return;
    summary.current?.focus();
  }, [tick]);

  const edit = (f: string, v: string) => { setText((s) => ({ ...s, [f]: v })); setErrors((e) => { const { [f]: _, ...r } = e; return r; }); };
  const failed = (next: Fail, errs: Record<string, string> = {}) => { setErrors(errs); setFail(next); setTick((n) => n + 1); };

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (busy || forbidden || fail?.kind === "conflict" || !reason.trim()) return;
    const all = cells();
    const bad = checkFields(t, all);
    if (Object.keys(bad).length) { failed({ kind: "fields" }, Object.fromEntries(Object.entries(bad).map(([f, r]) => [f, reasonLabel(r)]))); return; }
    setBusy(true); setFail(null); setErrors({});
    try {
      const res = await createManualEntry({
        sourceType: t, operation: correct ? "correct" : "create", ...(correct ? { recordKey: target.record!.recordKey } : {}),
        fields: all, basedOnUploadId: target.basedOn?.uploadId ?? null, justification: reason.trim(),
      });
      const notice: ManualNotice = { uploadId: res.uploadId, operation: correct ? "correct" : "create", count: correct ? res.changedCount : res.newCount, recordKey: res.recordKey, sourceType: t };
      go(uploadHref(res.uploadId), false, { manualEntry: notice });
    } catch (err) {
      setBusy(false);
      if (!(err instanceof ApiFailure)) { failed({ kind: "text", text: errorText(err) }); return; }
      const b = err.body;
      if (err.status === 409 && b.code === "VERSION_CONFLICT") {
        const latestId = Number(b.latestUploadId);
        const label = await getUpload(latestId).then((u) => versionLabel(u.origin, u.uploadId), () => `#${latestId}`);
        failed({ kind: "conflict", latestId, label });
      } else if (b.code === "ROW_ERRORS") {
        failed({ kind: "fields" }, Object.fromEntries(((b.rowErrors as RowError[] | undefined) ?? []).map((r) => [r.column, reasonLabel(r.reason)])));
      } else if (b.code === "JUSTIFICATION_REQUIRED") failed({ kind: "fields" }, { justification: "Gerekçe yazın." });
      else if (b.code === "RECORD_EXISTS") failed({ kind: "fields" }, { [inputs[0]!]: "Bu kimlikle bir kayıt zaten var. Değerini değiştirmek için kaydı Düzelt ile açın." });
      else if (b.code === "NO_CHANGE") failed({ kind: "text", text: "Kayıtta değişiklik yok." });
      else if (b.code === "NOT_FOUND") failed({ kind: "text", text: "Bu kayıt son versiyonda yok. Sayfayı yenileyip tekrar deneyin." });
      else if (err.status === 403) { setForbidden(true); failed({ kind: "text", text: "Bu işlem için yetkiniz yok." }); }
      else failed({ kind: "text", text: errorText(err) });
    }
  }

  const errorCount = Object.keys(errors).length;
  const idOf = (f: string) => `me-${f}`;
  const labelOf = (f: string) => (f === "justification" ? "Gerekçe" : fieldLabel(f));
  const close = () => { if (!busy) onClose(); };

  function field(f: string): ReactNode {
    const id = idOf(f), req = isRequired(t, f, cells()), hint = FIELD_HINT[`${t}.${f}`], kind = kinds[f], err = errors[f];
    const changed = correct && JSON.stringify(value(f)) !== JSON.stringify(target.record!.fields[f] ?? null);
    const described = [hint ? `${id}-hint` : "", err ? `${id}-err` : ""].filter(Boolean).join(" ") || undefined;
    const common = { id, value: text[f] ?? "", "aria-required": req || undefined, "aria-invalid": err ? true : undefined, "aria-describedby": described, onChange: (e: { target: { value: string } }) => edit(f, e.target.value) };
    const control = kind === "unit" || kind! in ENUMS ? (
      <NativeSelect {...common} className="w-full">
        <option value="">Seçin</option>
        {(kind === "unit" ? unitOptions().map((u) => ({ value: u, label: u })) : valueOptions(kind!)).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </NativeSelect>
    ) : kind === "number" ? <Input {...common} inputMode="decimal" placeholder="ör. 1.234,5" className="text-right tabular-nums" />
      : kind === "date" ? <Input {...common} type="date" /> : kind === "time" ? <Input {...common} type="time" />
      : <Input {...common} className={(spec.key as readonly string[]).includes(f) ? "font-mono" : undefined} />;
    return (
      <div key={f} className="flex flex-col gap-2">
        <Label htmlFor={id} className="min-h-6"><span>{fieldLabel(f)}{req && <span aria-hidden="true"> *</span>}</span>{changed && <StatusBadge tone="warning" className="ml-2">Değişti</StatusBadge>}</Label>
        {control}
        {hint && <p id={`${id}-hint`} className="text-xs text-muted-foreground">{hint}</p>}
        {err && <p id={`${id}-err`} className="text-sm text-destructive">{err}</p>}
      </div>
    );
  }

  const base = target.basedOn ? <>Dayanılan versiyon: <span className="tabular-nums">{versionLabel(target.basedOn.origin, target.basedOn.uploadId)}</span></> : "Bu kaynak tipinin ilk versiyonu olur.";
  const jErr = errors.justification;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-h-[90svh] overflow-y-auto sm:max-w-2xl" onInteractOutside={(e) => e.preventDefault()}
        onOpenAutoFocus={(e) => { e.preventDefault(); form.current?.querySelector<HTMLElement>("input, select, textarea")?.focus(); }}
        onCloseAutoFocus={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{correct ? `Kaydı düzelt — ${recordIdText(t, target.record!.recordKey)}` : `Yeni kayıt — ${sourceTypeText(t)}`}</DialogTitle>
          <DialogDescription className="text-xs">{correct ? `${sourceTypeText(t)} · ` : ""}{base}</DialogDescription>
          <p className="text-sm">{correct ? INFO.correct : INFO.create}</p>
        </DialogHeader>
        <form ref={form} noValidate aria-busy={busy} onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
          {fail && (
            <Alert ref={summary} variant="destructive" tabIndex={-1}>
              <AlertDescription>
                {fail.kind === "fields" && (
                  <>
                    <p>Kayıt kaydedilmedi.{errorCount > 0 && <> <span className="tabular-nums">{errorCount}</span> alanda hata var.</>}</p>
                    <ul className="list-disc pl-5">
                      {Object.entries(errors).map(([f, m]) => <li key={f}><a href={`#${idOf(f)}`} className={LINK} onClick={(e) => { e.preventDefault(); document.getElementById(idOf(f))?.focus(); }}>{labelOf(f)}</a>: {m}</li>)}
                    </ul>
                  </>
                )}
                {fail.kind === "text" && <ErrorLines text={fail.text} />}
                {fail.kind === "conflict" && (
                  <>
                    <p>Bu kaynak tipine yeni bir versiyon geldi. Sayfayı yenileyip tekrar deneyin.</p>
                    <p>Son versiyon: {fail.label}.</p>
                    <Button type="button" variant="outline" size="sm" className="text-foreground" onClick={() => go(uploadHref(fail.latestId))}>Son versiyonu aç</Button>
                  </>
                )}
              </AlertDescription>
            </Alert>
          )}
          {correct && (
            <div className="flex flex-col gap-2">
              <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1 rounded-md border bg-muted p-3 text-sm">
                {(spec.key as readonly string[]).map((k, i) => <div key={k} className="contents"><dt className="text-muted-foreground">{fieldLabel(k)}</dt><dd className="font-mono">{parts[i]}</dd></div>)}
              </dl>
              <p className="text-xs text-muted-foreground">Kimlik alanları düzeltilmez. Kimliği yanlış olan kaydı yeni kayıt olarak girin; eski kayıt kalır.</p>
            </div>
          )}
          <p className="text-xs text-muted-foreground">* zorunlu alan</p>
          <div className="grid gap-4 sm:grid-cols-2">
            {inputs.map(field)}
            <div className="flex flex-col gap-2 sm:col-span-2">
              <Label htmlFor="me-justification" className="min-h-6"><span>Gerekçe<span aria-hidden="true"> *</span></span></Label>
              <Textarea id="me-justification" rows={3} maxLength={4000} value={reason} aria-required="true" aria-invalid={jErr ? true : undefined}
                aria-describedby={`me-justification-hint${jErr ? " me-justification-err" : ""}`}
                onChange={(e) => { setReason(e.target.value); setErrors((s) => { const { justification: _, ...r } = s; return r; }); }} />
              <p id="me-justification-hint" className="text-xs text-muted-foreground">Veri Geçmişi'nde ve denetim raporunda görünür. Gerekçe yazılmadan kaydedilmez.</p>
              {jErr && <p id="me-justification-err" className="text-sm text-destructive">{jErr}</p>}
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" disabled={busy} onClick={close}>Vazgeç</Button>
            <Button type="submit" aria-describedby="me-justification-hint" disabled={busy || forbidden || fail?.kind === "conflict" || !reason.trim()}>{busy ? "Kaydediliyor…" : "Kaydet"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
